import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [studios, setStudios] = useState([]);
  const [activeStudio, setActiveStudio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingSelection, setPendingSelection] = useState(null);

  const getToken = () => localStorage.getItem('token');

  const fetchMe = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      setActiveStudio({
        studio_id: res.data.studio_id,
        role: res.data.role,
      });
      // Fetch studios list
      if (res.data.studios_count > 1) {
        const sRes = await axios.get('/auth/my-studios', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStudios(sRes.data.studios);
      }
    } catch {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post('/auth/login', { email, password });
    const data = res.data;

    if (data.requires_studio_selection) {
      setPendingSelection(data);
      return { requiresSelection: true, studios: data.studios };
    }

    localStorage.setItem('token', data.token);
    localStorage.setItem('studio_id', data.user.studio_id);
    setUser(data.user);
    setActiveStudio({
      studio_id: data.user.studio_id,
      role: data.user.role,
      ...data.studio,
    });
    setPendingSelection(null);
    return { requiresSelection: false };
  }, []);

  const selectStudio = useCallback(async (studioId) => {
    if (!pendingSelection) throw new Error('No pending selection');
    const res = await axios.post('/auth/select-studio', {
      user_id: pendingSelection.user_id,
      studio_id: studioId,
    });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('studio_id', res.data.user.studio_id);
    setUser(res.data.user);
    setActiveStudio({
      studio_id: res.data.user.studio_id,
      role: res.data.user.role,
      ...res.data.studio,
    });
    setPendingSelection(null);
    // Fetch all studios
    const sRes = await axios.get('/auth/my-studios', {
      headers: { Authorization: `Bearer ${res.data.token}` },
    });
    setStudios(sRes.data.studios);
  }, [pendingSelection]);

  const switchStudio = useCallback(async (studioId) => {
    const token = getToken();
    const res = await axios.post('/auth/switch-studio',
      { studio_id: studioId },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('studio_id', res.data.user.studio_id);
    setUser(res.data.user);
    setActiveStudio({
      studio_id: res.data.user.studio_id,
      role: res.data.user.role,
      ...res.data.studio,
    });
    // Refresh studios list
    const sRes = await axios.get('/auth/my-studios', {
      headers: { Authorization: `Bearer ${res.data.token}` },
    });
    setStudios(sRes.data.studios);
  }, []);

  const logout = useCallback(async () => {
    const token = getToken();
    if (token) {
      try {
        await axios.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        if (error.response?.status !== 401) {
          toast.error('Could not securely sign out. Please try again.');
          return false;
        }
      }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('studio_id');
    setUser(null);
    setStudios([]);
    setActiveStudio(null);
    setPendingSelection(null);
    return true;
  }, []);

  return (
    <AuthContext.Provider value={{
      user, studios, activeStudio, loading,
      pendingSelection,
      login, selectStudio, switchStudio, logout, fetchMe,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}
